import { LogOut, User as UserIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/use-auth.js';



import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.jsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.jsx';
import { ROUTES } from '@/config/routes.js';


function initialsOf(user) {
  const source = user?.name ?? user?.email ?? '?';
  return source.slice(0, 2).toUpperCase();
}

// Radix DropdownMenu + Button + asChild combined with the sticky/z-index
// topbar was failing to measure the trigger rect and leaving the menu
// stuck at its initial `translate(0, -200%)` hidden state. Using a plain
// <button> as the direct trigger (no asChild indirection) avoids the
// Slot/ref-forwarding path that was losing the reference.
export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const impersonating = Boolean(user?.impersonation?.is_impersonating);
  const impersonatedEmail = user?.impersonation?.impersonated_user?.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Account menu"
        className="relative inline-flex h-12 w-12 items-center justify-center rounded-full p-1 outline-none ring-offset-background transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Avatar className="h-9 w-9">
          <AvatarImage src={user?.avatar_url} alt="" />
          <AvatarFallback>{initialsOf(user)}</AvatarFallback>
        </Avatar>
        {impersonating && (
          <span
            title={impersonatedEmail ? `Impersonating: ${impersonatedEmail}` : 'Impersonating'}
            className="absolute right-1 top-1 h-3 w-3 rounded-full border-2 border-background bg-amber-500"
          />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="min-w-40">
        {user?.name && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            Signed in as <span className="font-medium text-foreground">{user.login ?? user.name}</span>
          </div>
        )}
        {impersonating && impersonatedEmail && (
          <div className="px-2 py-1.5 text-xs text-amber-600 dark:text-amber-500">
            Impersonating <span className="font-medium">{impersonatedEmail}</span>
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate(ROUTES.ADMIN.PROFILE)}>
          <UserIcon className="mr-2 h-4 w-4" /> Profile
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => {
            await logout();
            // ROUTES.ROOT is served by Astro outside this router; use a
            // real navigation so the user lands on the marketing
            // homepage rather than the SPA's 404 fallback.
            window.location.assign(ROUTES.ROOT);
          }}
        >
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
